import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';

import ConfirmDialog from './ConfirmDialog';
import { TextField } from '../forms';
import { c } from '../../styles/theme';

const MIN_PASSWORD_LENGTH = 6;

export interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: { currentPassword: string; newPassword: string }) => void;
  error?: string | null;
  pending?: boolean;
}

export default function ChangePasswordModal({
  open,
  onClose,
  onConfirm,
  error = null,
  pending = false,
}: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const close = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH;
  const canConfirm =
    currentPassword.length > 0 &&
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    confirmPassword === newPassword &&
    !pending;

  return (
    <ConfirmDialog
      open={open}
      onClose={close}
      title="Change password"
      icon={<KeyRound size={19} color={c.accent} />}
      showClose
      confirmLabel={pending ? 'Changing…' : 'Change password'}
      confirmIcon={<KeyRound size={13} />}
      confirmDisabled={!canConfirm}
      onConfirm={() => {
        onConfirm({ currentPassword, newPassword });
        reset();
      }}
    >
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px', lineHeight: 1.5 }}>
        Enter your current password, then choose a new one.
      </p>

      <TextField
        id="lyx-password-current"
        label="Current password"
        type="password"
        value={currentPassword}
        onChange={setCurrentPassword}
        placeholder="Current password"
        marginBottom={10}
      />

      <TextField
        id="lyx-password-new"
        label="New password"
        type="password"
        value={newPassword}
        onChange={setNewPassword}
        placeholder="New password"
        marginBottom={10}
      />

      <TextField
        id="lyx-password-confirm"
        label="Confirm new password"
        type="password"
        value={confirmPassword}
        onChange={setConfirmPassword}
        placeholder="Repeat new password"
        marginBottom={16}
      />

      {mismatch ? (
        <p role="alert" style={{ fontSize: 11.5, color: c.danger, margin: '0 0 16px' }}>
          New passwords do not match.
        </p>
      ) : tooShort ? (
        <p style={{ fontSize: 11.5, color: c.text2, margin: '0 0 16px' }}>
          Password must be at least {MIN_PASSWORD_LENGTH} characters.
        </p>
      ) : error ? (
        <p role="alert" style={{ fontSize: 11.5, color: c.danger, margin: '0 0 16px' }}>
          {error}
        </p>
      ) : (
        <p style={{ fontSize: 11.5, color: c.text2, margin: '0 0 16px' }}>
          Use at least {MIN_PASSWORD_LENGTH} characters.
        </p>
      )}
    </ConfirmDialog>
  );
}
